import { ProductVariantStateItem, ProductVariantTableState } from '@/lib/types/store/product-variants';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

/**
 * Interface for variant table item updates
 */
export interface ProductVariantTableItemUpdate {
  id: string;
  field: keyof ProductVariantStateItem;
  value: any;
}

/**
 * Initial state for the product variant table
 */
const initialState: ProductVariantTableState = {
  productId: null,
  items: [],
  selectedKeys: [],
  editingId: null,

  // Loading States
  loading: false,
  saving: false,
  deleting: false,

  // Pagination
  page: 1,
  pageSize: 10,
  totalItems: 0,

  // Sorting
  sortField: 'updatedAt',
  sortDirection: 'desc',

  // Error State
  error: null,
};

/**
 * Product Variant Table Slice
 * Redux slice for the product variant table
 */
const productVariantTableSlice = createSlice({
  name: 'productVariantTable',
  initialState,
  reducers: {
    // Data Actions
    setProductId: (state, action: PayloadAction<string | null>) => {
      state.productId = action.payload;

      // Reset state when product changes
      if (state.productId !== action.payload) {
        state.items = [];
        state.selectedKeys = [];
        state.editingId = null;
        state.page = 1;
      }
    },
    setVariants: (state, action: PayloadAction<ProductVariantStateItem[]>) => {
      state.items = action.payload;
      state.loading = false;
      state.error = null;
    },

    // Single Variant Actions
    updateVariant: (state, action: PayloadAction<ProductVariantTableItemUpdate>) => {
      const { id, field, value } = action.payload;
      const index = state.items.findIndex(item => item.id === id);

      if (index !== -1) {
        // @ts-expect-error - field is a valid key of ProductVariantStateItem
        state.items[index][field] = value;
      }
    },

    // UI State Actions
    setSelectedKeys: (state, action: PayloadAction<string[]>) => {
      state.selectedKeys = action.payload;
    },
    setEditingId: (state, action: PayloadAction<string | null>) => {
      state.editingId = action.payload;
    },

    // Loading State Actions
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setSaving: (state, action: PayloadAction<boolean>) => {
      state.saving = action.payload;
    },
    setDeleting: (state, action: PayloadAction<boolean>) => {
      state.deleting = action.payload;
    },

    // Pagination Actions
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    setTotalItems: (state, action: PayloadAction<number>) => {
      state.totalItems = action.payload;
    },

    // Sorting Actions
    setSortField: (state, action: PayloadAction<string>) => {
      state.sortField = action.payload;
    },
    setSortDirection: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortDirection = action.payload;
    },

    // Error State Actions
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
      state.saving = false;
      state.deleting = false;
    },

    // Reset Actions
    resetState: () => initialState,
  },
});

// Export actions
export const {
  setProductId,
  setVariants,
  updateVariant,
  setSelectedKeys,
  setEditingId,
  setLoading,
  setSaving,
  setDeleting,
  setPage,
  setPageSize,
  setTotalItems,
  setSortField,
  setSortDirection,
  setError,
  resetState,
} = productVariantTableSlice.actions;

// Export reducer
export default productVariantTableSlice.reducer;
